/**
 * ProcessExtractionProfile — DomainDataProfile for the `process` folder
 * domain that backs Compass extractions.
 *
 * Wired into the `OntologyPropertyMapper` so RDM-extracted nodes get
 * `attrs` populated from this declarative mapping (rather than ad-hoc
 * accesses scattered across hydrators). See
 * `docs/architecture/ontology-property-mapping.md`.
 *
 * Node-type names track the existing extraction graph emitted by
 * `ExtractionGraphService` (`process`, `control`, `actor`, `artifact`,
 * `risk`, `obligation`) — not the aspirational `Activity` /
 * `Actor(system)` taxonomy from the architecture doc, which is a
 * separate naming-migration project.
 */

import type { DomainDataProfile } from '../../../core/data-profiles';
import type { DiagramType } from '../../../lib/diagram/registries';

type ProcessNodeType =
  | 'process'
  | 'control'
  | 'actor'
  | 'artifact'
  | 'risk'
  | 'obligation'
  | 'system';

type ProcessEdgeType =
  | 'controlled_by'
  | 'evidenced_by'
  | 'owned_by'
  | 'depends_on'
  | 'mitigates'
  | 'assessed_at';

export const ProcessExtractionProfile: DomainDataProfile<ProcessNodeType, ProcessEdgeType> = {
  domainId: 'process',
  profileVersion: '1.0',
  diagramTypes: ['flowchart' as DiagramType],
  nodeTypes: ['process', 'control', 'actor', 'artifact', 'risk', 'obligation', 'system'],
  edgeTypes: ['controlled_by', 'evidenced_by', 'owned_by', 'depends_on', 'mitigates', 'assessed_at'],
  vocabulary: {
    recordSingular: 'process',
    recordPlural: 'processes',
    repositoryLabel: 'Process repository',
    hierarchyLabel: 'Process catalog',
    importAction: 'Import process',
    keyColumnHint: 'Name',
    commitLabel: 'Push processes',
    pullLabel: 'Refresh processes',
    diagramLabel: 'process map',
  },
  fieldAliases: {
    name: ['title', 'processName', 'controlName', 'label'],
    owner: ['responsible', 'accountable', 'team'],
    description: ['summary', 'narrative'],
    sourcePage: ['page', 'pageNumber'],
  },
  requiredProps: ['name'],
  recommendedProps: ['owner', 'description'],
  primaryShapeType: 'process',
  // Drives OntologyPropertyMapper. Editor-runtime `attrs` keys on the
  // right; ontology-typed `props` keys on the left. Add entries as
  // editor panels start consuming new fields.
  propsToAttrsMapping: {
    name: 'name',
    label: 'name',
    owner: 'owner',
    description: 'description',
    sourcePage: 'sourcePage',
    sourceDocumentId: 'sourceDocumentId',
    snippet: 'snippet',
    severity: 'severity',
    likelihood: 'likelihood',
    controlObjective: 'controlObjective',
    frameworkRef: 'frameworkRef',
    obligationType: 'obligationType',
    evidenceType: 'evidenceType',
    subtype: 'subtype',
    resourceType: 'resourceType',
    region: 'region',
  },
};
